import React from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import GridIcon from 'dibs-vg/dist/react/grid-empty';
import MessageIcon from 'dibs-vg/dist/react/message';
import TagIcon from 'dibs-vg/dist/react/tag-empty';
import get from 'lodash.get';
import classnames from 'classnames';
import {
    CreateProjectModal,
    DeleteProjectModal,
    UpdateInstallDateModal,
    UpdateNameModal,
    UpdateShippingAddressModal,
} from 'dibs-project-forms';

import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { loadSourcingModal } from 'dibs-buyer-layout/src/utils/tradeOnboardingHelper';

import actions from '../../actions/FavoritesActions';
import { tabs } from '../../constants';
import trackPageView from '../../helpers/trackPageView';
import Header from '../Headers/BasicHeader';
import InfoItem from './InfoItem';
import ProjectRow from './ProjectRow';
import Recommendations from '../Recommendations/Recommendations';

import styles from './ProjectsContainer.scss';

const messages = {
    filterByProject: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.filterByProject"
            defaultMessage="Filter by Projects"
        />
    ),
    filterByProjectExplanation: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.filterByProjectExplanation"
            defaultMessage="Easily filter tagged items in My Conversations and My Favorites."
        />
    ),
    projectsInfoHeader: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.projectsInfoHeader"
            defaultMessage="How it Works"
        />
    ),
    projectsTitle: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.projectsTitle"
            defaultMessage="All Projects"
        />
    ),
    tagItems: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.tagItems"
            defaultMessage="Tag Items"
        />
    ),
    tagItemsExplanation: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.tagItemsExplanation"
            defaultMessage="Easily filter tagged items in My Conversations and My Favorites."
        />
    ),
    viewProjects: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.viewProjects"
            defaultMessage="View All Projects"
        />
    ),
    viewProjectsExplanation: (
        <FormattedMessage
            id="favoritesNode.ProjectsContainer.viewProjectsExplanation"
            defaultMessage="Organize tags on this page and view all your projects at a glance."
        />
    ),
};

const projectModalTypes = {
    CREATE: 'CREATE',
    EDIT_NAME: 'EDIT_NAME',
    EDIT_INSTALL_DATE: 'EDIT_INSTALL_DATE',
    EDIT_SHIPPING_ADDRESS: 'EDIT_SHIPPING_ADDRESS',
    DELETE: 'DELETE',
};

const refetchOptions = {
    force: true,
};

class ProjectsContainer extends React.Component {
    constructor(props) {
        super(props);

        this.closeModal = this.closeModal.bind(this);
        this.handleCreateProjectSuccess = this.handleCreateProjectSuccess.bind(this);
        this.handleDeleteProjectSuccess = this.handleDeleteProjectSuccess.bind(this);
        this.info = this.info.bind(this);
        this.launchModal = this.launchModal.bind(this);
        this.linkProject = this.linkProject.bind(this);
        this.modal = this.modal.bind(this);
        this.projects = this.projects.bind(this);
        this.projectsTable = this.projectsTable.bind(this);

        this.state = {
            editingProjectId: null,
            inEditMode: false, //applies to mobile only
            openModal: null,
        };
    }

    componentDidMount() {
        trackPageView();
    }

    closeModal() {
        this.setState({
            editingProjectId: null,
            openModal: null,
        });
    }

    handleCreateProjectSuccess({ project, sourcingAssistanceRequested } = {}) {
        this.closeModal();
        this.props.relay.refetch(
            fragmentVariables => fragmentVariables,
            null,
            () => {
                if (sourcingAssistanceRequested) {
                    loadSourcingModal({
                        projectId: get(project, 'serviceId'),
                        projectName: get(project, 'name'),
                        projectInstallDate: get(project, 'installDate'),
                        requestOrigin: 'projects',
                        trackingLabel: 'project-create',
                    });
                }
            },
            refetchOptions
        );
    }

    handleDeleteProjectSuccess() {
        this.closeModal();
        this.props.relay.refetch(
            fragmentVariables => fragmentVariables,
            null,
            null,
            refetchOptions
        );
    }

    info() {
        return (
            <div>
                <Recommendations
                    onCreateProjectClick={() => this.launchModal(projectModalTypes.CREATE)}
                    trending={null}
                />
                <div className={`${styles.infoContainer} primary-viewport-width`}>
                    <BarHeader title={messages.projectsInfoHeader} />
                    <div className={`${styles.infoItems} primary-page-width`}>
                        <InfoItem
                            Icon={TagIcon}
                            copyPrimary={messages.tagItems}
                            copySecondary={messages.tagItemsExplanation}
                        />
                        <InfoItem
                            Icon={MessageIcon}
                            copyPrimary={messages.filterByProject}
                            copySecondary={messages.filterByProjectExplanation}
                        />
                        <InfoItem
                            Icon={GridIcon}
                            copyPrimary={messages.viewProjects}
                            copySecondary={messages.viewProjectsExplanation}
                        />
                    </div>
                </div>
            </div>
        );
    }

    launchModal(modalType, projectId = null) {
        this.setState({
            editingProjectId: projectId,
            openModal: modalType,
        });
    }

    linkProject(projectId, portfolioId) {
        return event => {
            event.preventDefault();

            if (!projectId || !portfolioId) {
                console.warn('cannot go to project without project id and portfolio id');
                return;
            }
            this.props.resetUiState();
            this.props.setProjectId(projectId);
            this.props.setPortfolioIds(portfolioId);
            this.props.setTab(tabs.PROJECT);
            this.props.setTabChanged(true);
        };
    }

    modal() {
        const { relay, userId, viewer } = this.props;
        const { editingProjectId, openModal } = this.state;
        const projectUnderEdit = editingProjectId
            ? this.projects().find(proj => get(proj, 'serviceId') === editingProjectId)
            : null;
        const firmId = get(viewer, 'user.tradeFirm.serviceId');
        const source = 'favorites_projects_index';

        switch (true) {
            case openModal === projectModalTypes.CREATE:
                return (
                    <CreateProjectModal
                        firmId={firmId}
                        isOpen
                        onClose={this.closeModal}
                        onSuccess={this.handleCreateProjectSuccess}
                        relayEnvironment={relay.environment}
                        source={source}
                        userId={userId}
                    />
                );
            case !!projectUnderEdit && openModal === projectModalTypes.DELETE:
                return (
                    <DeleteProjectModal
                        firmId={firmId}
                        isOpen
                        onClose={this.closeModal}
                        onSuccess={this.handleDeleteProjectSuccess}
                        relayEnvironment={relay.environment}
                        projectId={editingProjectId}
                        projectName={get(projectUnderEdit, 'name')}
                        source={source}
                        userId={userId}
                    />
                );
            case !!projectUnderEdit && openModal === projectModalTypes.EDIT_INSTALL_DATE:
                return (
                    <UpdateInstallDateModal
                        firmId={firmId}
                        isOpen
                        onClose={this.closeModal}
                        onSuccess={this.closeModal}
                        project={projectUnderEdit}
                        relayEnvironment={relay.environment}
                        source={source}
                        userId={userId}
                    />
                );
            case !!projectUnderEdit && openModal === projectModalTypes.EDIT_NAME:
                return (
                    <UpdateNameModal
                        firmId={firmId}
                        isOpen
                        onClose={this.closeModal}
                        onSuccess={this.closeModal}
                        project={projectUnderEdit}
                        relayEnvironment={relay.environment}
                        source={source}
                        userId={userId}
                    />
                );
            case !!projectUnderEdit && openModal === projectModalTypes.EDIT_SHIPPING_ADDRESS:
                return (
                    <UpdateShippingAddressModal
                        firmId={firmId}
                        isOpen
                        onClose={this.closeModal}
                        onSuccess={this.closeModal}
                        project={projectUnderEdit}
                        relayEnvironment={relay.environment}
                        source={source}
                        userId={userId}
                    />
                );
            default:
                return null;
        }
    }

    projects() {
        const edges = get(this.props, 'viewer.projects.edges');
        return Array.isArray(edges) ? edges.map(edge => get(edge, 'node')) : [];
    }

    projectsTable() {
        return (
            <div className={styles.projectsTable}>
                <div className={styles.projectsTableHeader}>
                    <div className={styles.categoryName}>
                        <FormattedMessage
                            id="favoritesNode.ProjectsContainer.columnHeaderProjectTag"
                            defaultMessage="Project Tag"
                        />
                    </div>
                    <div className={styles.categoryInstallDate}>
                        <FormattedMessage
                            id="favoritesNode.ProjectsContainer.columnHeaderInstallDate"
                            defaultMessage="Install Date"
                        />
                    </div>
                    <FormattedMessage
                        id="favoritesNode.ProjectsContainer.columnHeaderShippingAddress"
                        defaultMessage="Shipping Address"
                    />
                </div>
                <div className={styles.projectsTableBody}>
                    {this.projects().map(project => {
                        const id = project.serviceId;
                        return (
                            <ProjectRow
                                {...project}
                                forceEditMode={this.props.isMobile}
                                goToProjectPage={this.linkProject(
                                    id,
                                    get(project, 'portfolio.serviceId')
                                )}
                                onDeleteClick={() => this.launchModal(projectModalTypes.DELETE, id)}
                                onEditAddressClick={() =>
                                    this.launchModal(projectModalTypes.EDIT_SHIPPING_ADDRESS, id)
                                }
                                onEditDateClick={() =>
                                    this.launchModal(projectModalTypes.EDIT_INSTALL_DATE, id)
                                }
                                onEditNameClick={() =>
                                    this.launchModal(projectModalTypes.EDIT_NAME, id)
                                }
                                key={id}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        const projects = this.projects();

        return (
            <div className={classnames('content', styles.container)}>
                {projects.length > 0 && (
                    <Header
                        onClick={() => this.launchModal(projectModalTypes.CREATE)}
                        title={messages.projectsTitle}
                        totalItems={projects.length}
                    />
                )}
                {projects.length > 0 ? this.projectsTable() : this.info()}
                {this.modal()}
            </div>
        );
    }
}

ProjectsContainer.propTypes = {
    isMobile: PropTypes.bool,
    relay: PropTypes.object,
    resetUiState: PropTypes.func.isRequired,
    setProjectId: PropTypes.func.isRequired,
    setPortfolioIds: PropTypes.func.isRequired,
    setTab: PropTypes.func.isRequired,
    setTabChanged: PropTypes.func.isRequired,
    userId: PropTypes.string,
    viewer: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
    resetUiState: () => {
        dispatch(actions.resetUiState());
    },
    setPortfolioIds: portfolioId => {
        dispatch(actions.setPortfolioIds({ portfolioIds: [portfolioId] }));
    },
    setProjectId: projectId => {
        dispatch(actions.setProjectId({ projectId }));
    },
    setTab: tab => {
        dispatch(actions.setTab({ tab }));
    },
    setTabChanged: tabChanged => {
        dispatch(actions.setTabChanged({ tabChanged }));
    },
});

const mapStateToProps = state => ({ isMobile: state.favorites.uiState.deviceType === 'mobile' });
const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(ProjectsContainer);

ProjectsContainer = createRefetchContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment ProjectsContainer_viewer on Viewer {
                projects(userIds: $userIds, first: 200) @include(if: $hasUserId) {
                    edges {
                        node {
                            serviceId
                            createdDate
                            name
                            installDate
                            portfolio {
                                serviceId
                                items(first: 10) {
                                    edges {
                                        node {
                                            id
                                        }
                                    }
                                }
                            }
                            shippingAddress {
                                country
                                city
                                formattedAddress
                                line1
                                state
                                zipCode
                            }
                        }
                    }
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    tradeFirm {
                        serviceId
                    }
                }
            }
        `,
    },
    graphql`
        query ProjectsContainerRefetchQuery(
            $userId: String!
            $hasUserId: Boolean!
            $userIds: [String]!
        ) {
            viewer {
                ...ProjectsContainer_viewer
            }
        }
    `
);

export default ProjectsContainer;
